import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { api, EmailRecipient } from '@/lib/api-client';
import { FormAnalytics } from '@/components/FormAnalytics';
import { FormFieldMapping } from '@/components/FormFieldMapping';

interface FormData {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  allowed_origins: string[] | null;
  rate_limit_per_hour: number;
}

interface EditFormState {
  name: string;
  description: string;
  is_active: boolean;
  allowed_origins: string;
  rate_limit_per_hour: number;
}

export function FormDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<FormData | null>(null);
  const [chats, setChats] = useState<any[]>([]);
  const [tokens, setTokens] = useState<any[]>([]);
  const [emailRecipient, setEmailRecipient] = useState<EmailRecipient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Email delivery state
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>({
    name: '',
    description: '',
    is_active: true,
    allowed_origins: '',
    rate_limit_per_hour: 100,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Test submission state
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isTestSubmitting, setIsTestSubmitting] = useState(false);
  const [testFormData, setTestFormData] = useState<Record<string, string>>({});

  const apiUrl = import.meta.env.VITE_API_URL || '';
  const webhookUrl = `${apiUrl}/api/webhook/${id}`;

  useEffect(() => {
    if (id) {
      loadFormData();
    }
  }, [id]);

  const loadFormData = async () => {
    if (!id) return;

    try {
      const [formData, chatsData, tokensData, emailData] = await Promise.all([
        api.getForm(id),
        api.getFormChats(id),
        api.getFormTokens(id),
        api.getFormEmail(id),
      ]);

      setForm(formData as FormData);
      setChats((chatsData as any).chats || []);
      setTokens((tokensData as any).tokens || []);
      setEmailRecipient(emailData.email_recipient);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load form data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateToken = async () => {
    if (!id) return;

    try {
      await api.createToken(id);
      toast({
        title: 'Success',
        description: 'Binding token created',
      });
      loadFormData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create token',
        variant: 'destructive',
      });
    }
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast({
      title: 'Copied',
      description: 'Webhook URL copied to clipboard',
    });
  };

  const getPlaceholderValue = (sourceField: string, label: string): string => {
    const lowerField = sourceField.toLowerCase();
    if (lowerField.includes('email')) return 'test@example.com';
    if (lowerField.includes('phone')) return '+1234567890';
    if (lowerField.includes('name')) return 'Test User';
    if (lowerField.includes('message') || lowerField.includes('comment')) return 'This is a test message from FormTo';
    if (lowerField.includes('company') || lowerField.includes('organization')) return 'Test Company';
    if (lowerField.includes('url') || lowerField.includes('website')) return 'https://example.com';
    if (lowerField.includes('subject')) return 'Test Subject';
    return `Sample ${label || sourceField}`;
  };

  const openTestModal = async () => {
    if (!id) return;

    // Try to fetch mapping config to pre-fill fields
    try {
      const mappings = await api.getFormMappings(id);
      const prefilled: Record<string, string> = {};

      if (mappings.config?.fields && mappings.config.fields.length > 0) {
        mappings.config.fields.forEach((field) => {
          if (field.show_in_message !== false) {
            prefilled[field.source_field] = getPlaceholderValue(field.source_field, field.label);
          }
        });
      }

      // If no fields from mapping, use defaults
      if (Object.keys(prefilled).length === 0) {
        prefilled['name'] = 'Test User';
        prefilled['email'] = 'test@example.com';
        prefilled['message'] = 'This is a test message from FormTo';
      }

      setTestFormData(prefilled);
    } catch {
      // Fallback to default fields
      setTestFormData({
        name: 'Test User',
        email: 'test@example.com',
        message: 'This is a test message from FormTo',
      });
    }

    setIsTestModalOpen(true);
  };

  const handleTestSubmit = async () => {
    if (!id || Object.keys(testFormData).length === 0) return;
    setIsTestSubmitting(true);

    try {
      const result = await api.testSubmit(id, testFormData);
      const channels = [];
      if (result.telegram) channels.push('Telegram');
      if (result.email) channels.push('Email');

      toast({
        title: 'Test Sent',
        description: `Test submission sent to ${channels.join(' and ')}`,
      });
      setIsTestModalOpen(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test submission',
        variant: 'destructive',
      });
    } finally {
      setIsTestSubmitting(false);
    }
  };

  const openEditModal = () => {
    if (form) {
      setEditForm({
        name: form.name,
        description: form.description || '',
        is_active: form.is_active,
        allowed_origins: form.allowed_origins?.join('\n') || '',
        rate_limit_per_hour: form.rate_limit_per_hour || 100,
      });
      setIsEditModalOpen(true);
    }
  };

  const handleUpdateForm = async () => {
    if (!id) return;
    setIsSubmitting(true);

    try {
      const origins = editForm.allowed_origins
        .split('\n')
        .map(o => o.trim())
        .filter(o => o.length > 0);

      await api.updateForm(id, {
        name: editForm.name,
        description: editForm.description,
        is_active: editForm.is_active,
        allowed_origins: origins.length > 0 ? origins : null,
        rate_limit_per_hour: editForm.rate_limit_per_hour,
      });

      toast({
        title: 'Success',
        description: 'Form updated successfully',
      });
      setIsEditModalOpen(false);
      loadFormData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update form',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteForm = async () => {
    if (!id) return;
    setIsSubmitting(true);

    try {
      await api.deleteForm(id);
      toast({
        title: 'Success',
        description: 'Form deleted successfully',
      });
      navigate('/app/forms');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete form',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleDisconnectChat = async (chatId: string) => {
    if (!id) return;

    try {
      await api.deleteFormChat(id, chatId);
      toast({
        title: 'Success',
        description: 'Chat disconnected successfully',
      });
      loadFormData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect chat',
        variant: 'destructive',
      });
    }
  };

  const handleRevokeToken = async (tokenId: string) => {
    try {
      await api.deleteToken(tokenId);
      toast({
        title: 'Success',
        description: 'Token revoked successfully',
      });
      loadFormData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to revoke token',
        variant: 'destructive',
      });
    }
  };

  // Email delivery handlers
  const handleAddEmail = async () => {
    if (!id || !newEmail.trim()) return;
    setIsEmailLoading(true);

    try {
      await api.addFormEmail(id, newEmail.trim());
      toast({
        title: 'Success',
        description: 'Verification code sent to your email',
      });
      setNewEmail('');
      loadFormData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add email',
        variant: 'destructive',
      });
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!id || !verificationCode.trim()) return;
    setIsEmailLoading(true);

    try {
      await api.verifyFormEmail(id, verificationCode.trim());
      toast({
        title: 'Success',
        description: 'Email verified successfully',
      });
      setVerificationCode('');
      loadFormData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Invalid verification code',
        variant: 'destructive',
      });
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!id) return;
    setIsEmailLoading(true);

    try {
      await api.resendEmailCode(id);
      toast({
        title: 'Success',
        description: 'New verification code sent',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend code',
        variant: 'destructive',
      });
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleRemoveEmail = async () => {
    if (!id) return;
    setIsEmailLoading(true);

    try {
      await api.removeFormEmail(id);
      toast({
        title: 'Success',
        description: 'Email removed successfully',
      });
      loadFormData();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove email',
        variant: 'destructive',
      });
    } finally {
      setIsEmailLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Form not found</p>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="mb-8">
          <Link to="/app/forms" className="text-blue-600 hover:underline mb-2 inline-block">
            ← Back to Forms
          </Link>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{form.name}</h1>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  form.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="text-gray-600">{form.description || 'No description'}</p>
            </div>
            <div className="flex gap-2">
              <Link to={`/app/forms/${id}/messages`}>
                <Button variant="outline">
                  View Messages
                </Button>
              </Link>
              <Button variant="outline" onClick={openEditModal}>
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setIsDeleteModalOpen(true)}>
                Delete
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Webhook URL</CardTitle>
              <CardDescription>Use this URL in your form's action attribute</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Input value={webhookUrl} readOnly />
                <Button onClick={handleCopyWebhook}>Copy</Button>
                <Button variant="outline" onClick={openTestModal}>
                  Send Test
                </Button>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-sm">
                <p className="font-mono text-xs overflow-x-auto">
                  {`<form action="${webhookUrl}" method="POST">`}<br />
                  {`  <input name="email" type="email" required />`}<br />
                  {`  <input name="message" type="text" required />`}<br />
                  {`  <button type="submit">Send</button>`}<br />
                  {`</form>`}
                </p>
              </div>
            </CardContent>
          </Card>

          {id && <FormAnalytics formId={id} />}

          {id && <FormFieldMapping formId={id} />}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Connected Chats</CardTitle>
                <CardDescription>Telegram chats receiving form submissions</CardDescription>
              </div>
              <Button onClick={handleCreateToken}>Create Binding Token</Button>
            </CardHeader>
            <CardContent>
              {chats.length === 0 ? (
                <p className="text-gray-500">No connected chats yet. Create a binding token to connect a Telegram chat.</p>
              ) : (
                <div className="space-y-2">
                  {chats.map((chat) => (
                    <div key={chat.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{chat.title || chat.username || `Chat ${chat.id}`}</p>
                        <p className="text-sm text-gray-500">{chat.type}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDisconnectChat(chat.id)}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Email Delivery</CardTitle>
              <CardDescription>Receive form submissions via email (in addition to Telegram)</CardDescription>
            </CardHeader>
            <CardContent>
              {emailRecipient ? (
                emailRecipient.is_verified ? (
                  // Verified state: show email with remove button
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-medium">{emailRecipient.email}</p>
                        <p className="text-sm text-green-600">Verified</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveEmail}
                      disabled={isEmailLoading}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  // Pending verification: show code input
                  <div className="space-y-4">
                    <div className="p-3 border rounded-lg bg-yellow-50">
                      <p className="text-sm text-yellow-800">
                        Verification code sent to <strong>{emailRecipient.email}</strong>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter 6-digit code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className="font-mono tracking-widest text-center"
                      />
                      <Button onClick={handleVerifyEmail} disabled={isEmailLoading || verificationCode.length !== 6}>
                        {isEmailLoading ? 'Verifying...' : 'Verify'}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={handleResendCode} disabled={isEmailLoading}>
                        Resend Code
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleRemoveEmail} disabled={isEmailLoading}>
                        Use Different Email
                      </Button>
                    </div>
                  </div>
                )
              ) : (
                // No email: show add form
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                    <Button onClick={handleAddEmail} disabled={isEmailLoading || !newEmail.trim()}>
                      {isEmailLoading ? 'Adding...' : 'Add Email'}
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">
                    A verification code will be sent to this email address.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Binding Tokens</CardTitle>
              <CardDescription>Send these tokens to @{import.meta.env.VITE_BOT_USERNAME || 'formtocc_bot'} to connect a chat</CardDescription>
            </CardHeader>
            <CardContent>
              {tokens.length === 0 ? (
                <p className="text-gray-500">No binding tokens yet</p>
              ) : (
                <div className="space-y-3">
                  {tokens.map((token) => {
                    const botUsername = import.meta.env.VITE_BOT_USERNAME || 'formtocc_bot';
                    const deepLink = token.methods?.deep_link || `https://t.me/${botUsername}?start=${token.token}`;

                    return (
                      <div key={token.id} className="p-4 border rounded-lg">
                        <div className="flex gap-4">
                          {/* QR Code - only for pending tokens */}
                          {token.status === 'pending' && (
                            <div className="flex-shrink-0">
                              <QRCodeSVG
                                value={deepLink}
                                size={100}
                                level="M"
                                includeMargin={false}
                              />
                            </div>
                          )}

                          {/* Token Details */}
                          <div className="flex-grow min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                token.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : token.status === 'used'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {token.status}
                              </span>
                              <span className="text-xs text-gray-500">
                                Expires: {new Date(token.expires_at).toLocaleString()}
                              </span>
                            </div>

                            <p className="font-mono text-sm text-gray-600 truncate mb-2">
                              {token.token}
                            </p>

                            {/* Action buttons - only for pending tokens */}
                            {token.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  variant="default"
                                  size="sm"
                                  asChild
                                >
                                  <a href={deepLink} target="_blank" rel="noopener noreferrer">
                                    Open in Telegram
                                  </a>
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRevokeToken(token.id)}
                                >
                                  Revoke
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      {/* Edit Form Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Form</DialogTitle>
            <DialogDescription>
              Update your form settings below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Form name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="is_active">Status</Label>
              <button
                type="button"
                role="switch"
                aria-checked={editForm.is_active}
                onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editForm.is_active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editForm.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">
                {editForm.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="allowed_origins">
                Allowed Origins
                <span className="text-xs text-gray-500 ml-2">(one per line)</span>
              </Label>
              <textarea
                id="allowed_origins"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={editForm.allowed_origins}
                onChange={(e) => setEditForm({ ...editForm, allowed_origins: e.target.value })}
                placeholder="https://example.com&#10;https://www.example.com&#10;*.example.com"
              />
              <p className="text-xs text-gray-500">
                Leave empty to allow all origins. Use * for wildcard subdomain matching.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="rate_limit">Rate Limit (requests per hour)</Label>
              <Input
                id="rate_limit"
                type="number"
                min="1"
                max="10000"
                value={editForm.rate_limit_per_hour}
                onChange={(e) => setEditForm({ ...editForm, rate_limit_per_hour: parseInt(e.target.value) || 100 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateForm} disabled={isSubmitting || !editForm.name.trim()}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Form</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{form?.name}"? This action cannot be undone.
              All connected chats and tokens will also be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteForm} disabled={isSubmitting}>
              {isSubmitting ? 'Deleting...' : 'Delete Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Submission Modal */}
      <Dialog open={isTestModalOpen} onOpenChange={setIsTestModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send Test Submission</DialogTitle>
            <DialogDescription>
              This will send a test submission to all connected chats and email.
              Test messages will be prefixed with "[TEST]".
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[400px] overflow-y-auto">
            {Object.entries(testFormData).map(([key, value]) => (
              <div key={key} className="grid gap-2">
                <Label htmlFor={`test-${key}`}>{key}</Label>
                <Input
                  id={`test-${key}`}
                  value={value}
                  onChange={(e) =>
                    setTestFormData({ ...testFormData, [key]: e.target.value })
                  }
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleTestSubmit} disabled={isTestSubmitting}>
              {isTestSubmitting ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
